/**
 * CRMCustomersPage — Lista clienti unificata (CRM-1, mag 2026).
 * Search globale debounced + drawer dettaglio con 4 tab.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Loader2, Search, Users, Plus, Phone, Mail } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function useDebouncedValue(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

export default function CRMCustomersPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 300);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("per_page", "50");
      if (debouncedQ.trim()) params.set("q", debouncedQ.trim());
      const r = await axios.get(`${API}/api/admin/crm/customers?${params.toString()}`, { withCredentials: true });
      setItems(r.data.items);
      setTotal(r.data.total);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedQ]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="crm-customers-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900 flex items-center gap-2">
            <Users className="w-5 h-5" /> Clienti
          </h2>
          <p className="text-sm text-zinc-500">{total} clienti totali</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowCreate(true)} data-testid="crm-customers-new-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-zinc-900 text-white hover:bg-zinc-800">
            <Plus className="w-4 h-4" /> Nuovo cliente
          </button>
          <a href={`${API}/api/admin/crm/export/customers?format=xlsx`} target="_blank" rel="noreferrer"
             className="px-3 py-1.5 text-sm rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
             data-testid="crm-customers-export-btn">Export</a>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
        <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
          placeholder="Cerca per nome, email, telefono, ragione sociale..."
          data-testid="crm-customers-search"
          className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-zinc-200 bg-white" />
      </div>

      {error && <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-700">{String(error)}</div>}

      <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5">Nome / Ragione sociale</th>
              <th className="text-left px-4 py-2.5 hidden md:table-cell">Email</th>
              <th className="text-left px-4 py-2.5 hidden md:table-cell">Telefono</th>
              <th className="text-center px-4 py-2.5">Stato</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {loading && items.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-zinc-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Caricamento...</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-zinc-400 italic">Nessun cliente trovato</td></tr>
            )}
            {items.map((c) => {
              const a = c.anagrafica || {};
              const nome = c.tipo === "azienda"
                ? (a.ragione_sociale || "—")
                : ([a.nome, a.cognome].filter(Boolean).join(" ") || "—");
              return (
                <tr key={c.id} onClick={() => setSelectedId(c.id)}
                  data-testid={`crm-customer-row-${c.id}`}
                  className="hover:bg-zinc-50/70 cursor-pointer transition-colors">
                  <td className="px-4 py-2.5 font-medium text-zinc-800">{nome}</td>
                  <td className="px-4 py-2.5 hidden md:table-cell text-zinc-600 text-xs">{a.email_norm || "—"}</td>
                  <td className="px-4 py-2.5 hidden md:table-cell text-zinc-600 text-xs">{a.telefono_norm || "—"}</td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                      c.stato_cliente === "attivo" ? "bg-emerald-100 text-emerald-700"
                      : c.stato_cliente === "potenziale" ? "bg-amber-100 text-amber-700"
                      : "bg-zinc-100 text-zinc-600"
                    }`}>{c.stato_cliente || "—"}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {total > 50 && (
        <div className="flex items-center justify-between text-sm text-zinc-600">
          <p>Pagina {page} di {Math.ceil(total / 50)}</p>
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
              className="px-2 py-1 rounded bg-zinc-100 disabled:opacity-40">← Precedente</button>
            <button onClick={() => setPage((p) => p + 1)} disabled={page * 50 >= total}
              className="px-2 py-1 rounded bg-zinc-100 disabled:opacity-40">Successiva →</button>
          </div>
        </div>
      )}

      {selectedId && <CustomerDrawer customerId={selectedId} onClose={() => { setSelectedId(null); load(); }} />}
      {showCreate && <CustomerCreateModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}

function CustomerDrawer({ customerId, onClose }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("anagrafica");

  useEffect(() => {
    axios.get(`${API}/api/admin/crm/customers/${customerId}`, { withCredentials: true })
      .then((r) => setData(r.data));
  }, [customerId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center md:justify-end" onClick={onClose} data-testid="crm-customer-drawer">
      <div className="bg-white w-full md:max-w-lg md:h-full overflow-y-auto rounded-t-2xl md:rounded-t-none md:rounded-l-2xl p-5" onClick={(e) => e.stopPropagation()}>
        {!data ? <Loader2 className="w-4 h-4 animate-spin" /> : (
          <>
            <h3 className="text-lg font-semibold mb-1">
              {data.customer.tipo === "azienda"
                ? data.customer.anagrafica.ragione_sociale
                : [data.customer.anagrafica.nome, data.customer.anagrafica.cognome].filter(Boolean).join(" ")}
            </h3>
            <p className="text-xs text-zinc-500 mb-3">Cliente {customerId.slice(0, 8)} · {data.customer.stato_cliente}</p>

            <div className="flex gap-1 border-b border-zinc-200 mb-3 text-xs">
              {["anagrafica", "leads", "policies", "documents"].map((t) => (
                <button key={t} onClick={() => setTab(t)} data-testid={`crm-customer-tab-${t}`}
                  className={`px-2.5 py-1.5 ${tab === t ? "border-b-2 border-zinc-900 font-semibold" : "text-zinc-500"}`}>
                  {t === "anagrafica" ? "Anagrafica" : t === "leads" ? "Lead" : t === "policies" ? "Polizze" : "Documenti"}
                </button>
              ))}
            </div>

            {tab === "anagrafica" && (
              <div className="space-y-1.5 text-sm">
                {data.customer.anagrafica.codice_fiscale && <p><strong>CF:</strong> <span className="font-mono text-xs">{data.customer.anagrafica.codice_fiscale}</span></p>}
                {data.customer.anagrafica.partita_iva && <p><strong>P.IVA:</strong> <span className="font-mono text-xs">{data.customer.anagrafica.partita_iva}</span></p>}
                {data.customer.anagrafica.email && <p className="flex items-center gap-1"><Mail className="w-3 h-3" /> {data.customer.anagrafica.email}</p>}
                {data.customer.anagrafica.telefono && <p className="flex items-center gap-1"><Phone className="w-3 h-3" /> {data.customer.anagrafica.telefono}</p>}
              </div>
            )}

            {tab === "leads" && (
              <div className="space-y-2 text-xs">
                {data.leads.length === 0 && <p className="italic text-zinc-400">Nessun lead</p>}
                {data.leads.map((l) => (
                  <div key={l.id} className="border border-zinc-200 rounded p-2">
                    <p><strong>{l.tipo_richiesta}</strong> · {l.stato_pipeline}</p>
                    <p className="text-zinc-500">{l.created_at?.slice(0, 10)}</p>
                  </div>
                ))}
              </div>
            )}

            {tab === "policies" && (
              <CustomerPoliciesTab customerId={customerId} />
            )}

            {tab === "documents" && (
              <p className="text-sm text-zinc-400 italic">Sarà popolato in CRM-3</p>
            )}

            <button onClick={onClose} className="mt-4 w-full py-2 text-sm rounded bg-zinc-100 hover:bg-zinc-200">Chiudi</button>
          </>
        )}
      </div>
    </div>
  );
}

function CustomerPoliciesTab({ customerId }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/admin/crm/customers/${customerId}/policies`, { withCredentials: true })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [customerId]);
  if (items === null) return <Loader2 className="w-4 h-4 animate-spin" />;
  if (items.length === 0) return <p className="italic text-zinc-400 text-xs">Nessuna polizza emessa</p>;
  return (
    <div className="space-y-2 text-xs" data-testid="crm-customer-policies-list">
      {items.map((p) => (
        <div key={p.id} className="border border-zinc-200 rounded p-2" data-testid={`crm-customer-policy-${p.id}`}>
          <p className="flex items-center justify-between">
            <strong className="font-mono">{p.numero_polizza}</strong>
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${p.stato === "attiva" ? "bg-emerald-100 text-emerald-700" : p.stato === "scaduta" ? "bg-rose-100 text-rose-700" : "bg-zinc-100 text-zinc-600"}`}>{p.stato}</span>
          </p>
          <p className="text-zinc-500">{p.compagnia} · {p.ramo} · scade {p.scadenza?.slice(0, 10)}</p>
          <p className="text-zinc-600">€ {Number(p.premio_lordo_eur || 0).toFixed(2)} · {p.frazionamento}</p>
        </div>
      ))}
    </div>
  );
}

function CustomerCreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ tipo: "privato" });
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API}/api/admin/crm/customers`, form, { withCredentials: true });
      onCreated();
    } catch (e) {
      setError(e.response?.data?.detail?.error_code || e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="crm-customer-create-modal">
        <h3 className="text-lg font-semibold mb-3">Nuovo cliente</h3>
        <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}
          className="w-full mb-2 px-3 py-2 text-sm rounded border border-zinc-200">
          <option value="privato">Privato</option>
          <option value="azienda">Azienda</option>
        </select>
        {form.tipo === "privato" ? (
          <>
            <input placeholder="Nome" onChange={(e) => setForm({ ...form, nome: e.target.value })} className="w-full mb-2 px-3 py-2 text-sm rounded border border-zinc-200" />
            <input placeholder="Cognome" onChange={(e) => setForm({ ...form, cognome: e.target.value })} className="w-full mb-2 px-3 py-2 text-sm rounded border border-zinc-200" />
            <input placeholder="Codice Fiscale" onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value })} className="w-full mb-2 px-3 py-2 text-sm rounded border border-zinc-200 font-mono" />
          </>
        ) : (
          <>
            <input placeholder="Ragione sociale" onChange={(e) => setForm({ ...form, ragione_sociale: e.target.value })} className="w-full mb-2 px-3 py-2 text-sm rounded border border-zinc-200" />
            <input placeholder="Partita IVA" onChange={(e) => setForm({ ...form, partita_iva: e.target.value })} className="w-full mb-2 px-3 py-2 text-sm rounded border border-zinc-200 font-mono" />
          </>
        )}
        <input placeholder="Email" onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full mb-2 px-3 py-2 text-sm rounded border border-zinc-200" />
        <input placeholder="Telefono" onChange={(e) => setForm({ ...form, telefono: e.target.value })} className="w-full mb-3 px-3 py-2 text-sm rounded border border-zinc-200" />
        {error && <p className="text-xs text-rose-600 mb-2">{String(error)}</p>}
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 text-sm rounded bg-zinc-100">Annulla</button>
          <button onClick={submit} disabled={submitting} data-testid="crm-customer-create-submit"
            className="flex-1 py-2 text-sm rounded bg-zinc-900 text-white disabled:opacity-50">
            {submitting ? "..." : "Crea"}
          </button>
        </div>
      </div>
    </div>
  );
}
